/**
 * Names as the API takes them, from whatever someone typed. A box for a
 * name accepts anything; what is sent is this: lower case, accents dropped,
 * every run of other characters one dash, starting with a letter or digit,
 * at most 63 characters. Nothing usable gives '' and the form says so.
 * Pure, no DOM: the console and the playground both use it.
 */

/** The API's own patterns (cloud/openapi.yaml). Every normalised name matches one, or is ''. */
export const WORKSPACE_PATTERN = '^[a-z0-9][a-z0-9-]{0,62}$';
export const WORLD_PATTERN = '^[a-z0-9][a-z0-9._-]{0,62}$';
export const GLOBAL_PATTERN = '^[a-z0-9][a-z0-9-]*/[a-z0-9][a-z0-9._-]*$';

/** What a form says when a name has nothing usable in it. */
export const NAME_EMPTY = 'Use at least one letter or number.';

const MAX = 63;

const normalise = (raw: string, outside: RegExp): string =>
  raw
    .trim()
    .normalize('NFKD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(outside, '-')
    .replace(/^[^a-z0-9]+/, '')
    .slice(0, MAX)
    .replace(/[-._]+$/, '');

/** A workspace: a–z 0–9 and dashes. */
export const workspaceName = (raw: string): string => normalise(raw, /[^a-z0-9]+/g);

/** A World, and a relay: a–z 0–9 and . _ - */
export const worldName = (raw: string): string => normalise(raw, /[^a-z0-9._-]+/g);

/**
 * `workspace/world`, split on the first slash. Without a slash it is a bare
 * World name, for boxes that take either. A side with nothing usable makes
 * the whole '' rather than half a name.
 */
export const globalName = (raw: string): string => {
  const slash = raw.indexOf('/');
  if (slash < 0) return worldName(raw);
  const workspace = workspaceName(raw.slice(0, slash));
  const world = worldName(raw.slice(slash + 1));
  return workspace && world ? `${workspace}/${world}` : '';
};

/** The normaliser for a schema pattern that is one of the API's name patterns, else null. */
export const nameFor = (pattern: unknown): ((raw: string) => string) | null =>
  pattern === WORKSPACE_PATTERN ? workspaceName
    : pattern === WORLD_PATTERN ? worldName
      : pattern === GLOBAL_PATTERN ? globalName
        : null;
